import { useToast } from '@material-bank/mb-uikit/shared/stores';
import { MutationOptions, useMutation } from '@tanstack/react-query';

import { ENDPOINT, MUTATION_KEY, apiClient } from '../../../shared/api';

export interface SendPasswordResetLinkParams {
  email: string;
}

export interface SendPasswordResetLinkSuccessResult {
  status: 'success';
  message: string;
}

export interface SendPasswordResetLinkFailedResult {
  status?: 'failed';
  message?: string;
}

export type SendPasswordResetLinkResult =
  | SendPasswordResetLinkSuccessResult
  | SendPasswordResetLinkFailedResult;

export const sendPasswordResetLink = async (
  params: SendPasswordResetLinkParams,
): Promise<SendPasswordResetLinkResult> => {
  const { data } = await apiClient.post<SendPasswordResetLinkResult>(
    ENDPOINT.SEND_PASSWORD_RESET_LINK,
    params,
  );

  return data;
};

export interface UseSendPasswordResetLinkMutationOptions
  extends MutationOptions<
    SendPasswordResetLinkResult,
    unknown,
    SendPasswordResetLinkParams,
    unknown
  > {
  onSuccessResult?(
    result: SendPasswordResetLinkSuccessResult,
    params: SendPasswordResetLinkParams,
  ): void;
  onFailedResult?(result: SendPasswordResetLinkFailedResult): void;
}

export const useSendPasswordResetLinkMutation = (
  options: UseSendPasswordResetLinkMutationOptions = {},
) => {
  const { onSuccessResult, onFailedResult, ...mutationOptions } = options;

  const { showErrorToast } = useToast();

  return useMutation({
    mutationKey: [MUTATION_KEY.SEND_PASSWORD_RESET_LINK],
    mutationFn: sendPasswordResetLink,
    ...mutationOptions,
    onSuccess: (result, params, context) => {
      if (result.status === 'success') {
        onSuccessResult?.(result, params);
      } else {
        showErrorToast({ collapsibleContent: result.message });
        onFailedResult?.(result);
      }

      options?.onSuccess?.(result, params, context);
    },
  });
};
